import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from '../../../../../../redux/connect';
import { difference as lodashDifference } from 'lodash';
import T from 'i18n-react';
import { WORKFLOWS_ROLE_SUBCONTRACTOR } from '../../../../../../constants/roles-types';
import { HQ_USER_TYPE, HQ_COMPANY_TYPE, HQ_ROLE_TYPE } from '../../../../../../constants/hq-types';
import { objectType, arrayType } from '../../../../../../constants/proptype-validation';
import AssignedToDropdown from './components/AssignedToDropdown';
import { getOxygenMappings, getCurrentProjectEntitiesWithActiveRoles, getInactiveRoles } from '../../../../../../redux/selectors';
import featureFlags, { FEATURE_FLAGS } from '../../../../../../utils/featureFlags';

class AdminAssignedToDropdown extends PureComponent {
  constructor() {
    super(...arguments);

    this.state = {
      selected: [],
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.getAllOptions = this.getAllOptions.bind(this);
  }

  componentWillMount() {
    this.setState({
      selected: this.parseInitialSelectedValues(this.props.serverData),
    });
  }

  get specialOptions() {
    if (!this._specialOptions) {
      this._specialOptions = [];
      if (this.props.name === WORKFLOWS_ROLE_SUBCONTRACTOR) {
        if (this.props.serverData.find((v) => v.type === HQ_COMPANY_TYPE && v.id === 'all')) {
          this._specialOptions.push({
            id: 'all',
            type: HQ_COMPANY_TYPE,
            text: T.translate('adminWorkflow.specialOption.allCompanies'),
          });
        } else {
          this._specialOptions.push({
            id: 'all',
            type: HQ_USER_TYPE,
            text: T.translate('adminWorkflow.specialOption.allUsers'),
          });
        }
      }
    }
    return this._specialOptions;
  }

  parseInitialSelectedValues(selectedValues) {
    const newSelectedValues = [];
    const companiesMap = this.props.oxygen.companies;
    selectedValues.forEach((selectedVal) => {
      if (selectedVal.type === 'trade') {
        const trade = selectedVal.id;
        const tradeCompaniesIds = Object.keys(companiesMap).filter((k) => companiesMap[k].trade === trade);
        tradeCompaniesIds.forEach((tradeCompanyId) => {
          if (!selectedValues.find((v) => v.type === HQ_COMPANY_TYPE && v.id === tradeCompanyId)) {
            newSelectedValues.push({
              type: HQ_COMPANY_TYPE,
              id: tradeCompanyId,
            });
          }
        });
      } else {
        newSelectedValues.push(selectedVal);
      }
    });
    return this.parseSelectedValues(newSelectedValues, []);
  }

  parseSelectedValues(newSelectedValues, oldSelectedValues) {
    let parsedSelectedValues = newSelectedValues.slice();

    const newSpecialTypesAll = newSelectedValues.filter((v) => v.id === 'all').map((v) => v.type);
    const oldSpecialTypesAll = oldSelectedValues.filter((v) => v.id === 'all').map((v) => v.type);

    const addedSpecialTypesAll = lodashDifference(newSpecialTypesAll, oldSpecialTypesAll);
    const removedSpecialTypesAll = lodashDifference(oldSpecialTypesAll, newSpecialTypesAll);

    const hqTypesOxygenMap = {
      [HQ_USER_TYPE]: this.props.oxygen.users,
      [HQ_COMPANY_TYPE]: this.props.oxygen.companies,
      [HQ_ROLE_TYPE]: this.props.oxygen.roles,
    };

    // go over the HQ types lists and select / deselect values to show
    Object.keys(hqTypesOxygenMap).forEach((hqType) => {
      if (addedSpecialTypesAll.includes(hqType)) {
        // select 'all' type from HQ list
        parsedSelectedValues = parsedSelectedValues.concat(
          ...Object.keys(hqTypesOxygenMap[hqType])
            .filter((oxygenId) => !parsedSelectedValues.find((v) => v.id === oxygenId && v.type === hqType))
            .map((oxygenId) => ({
              type: hqType,
              id: oxygenId,
            }))
        );
      } else if (removedSpecialTypesAll.includes(hqType)) {
        // deselect 'all' type from selected list
        parsedSelectedValues = parsedSelectedValues.filter((v) => v.type !== hqType);
      } else if (newSpecialTypesAll.includes(hqType)) {
        // check 'all' type values are still selected
        const countSpecialType = parsedSelectedValues.filter((v) => v.type === hqType).length - 1; // reduce id 'all' in counting
        const countOxygenType = Object.keys(hqTypesOxygenMap[hqType]).length;
        if (countSpecialType < countOxygenType) {
          // deselect special type 'all'
          parsedSelectedValues = parsedSelectedValues.filter((v) => v.id !== 'all' || v.type !== hqType);
        }
      }
    });

    return parsedSelectedValues;
  }

  getAssigneesList(selectedValues) {
    const specialTypesAll = selectedValues.filter((v) => v.id === 'all').map((v) => v.type);

    // remove entities of any type of selected special 'all'
    const assigneesList = selectedValues.filter((v) => v.id === 'all' || !specialTypesAll.includes(v.type));

    return assigneesList;
  }

  handleChange(valueArray) {
    this.setState({
      selected: this.parseSelectedValues(valueArray, this.state.selected),
    });
  }

  handleBlur() {
    const assigneesList = this.getAssigneesList(this.state.selected);
    if (lodashDifference(assigneesList, this.props.serverData).length || lodashDifference(this.props.serverData, assigneesList).length) {
      this.props.saveAssignees(assigneesList);
    }
  }

  getAllOptions() {
    // The options we need to show:
    // 1. All active roles
    // 2. Previously selected inactive roles
    // please note that if the user unchecked an inactive role - it should disappear from the dropdown
    const selectedRolesIds = this.state.selected.filter(assignee => assignee.type === HQ_ROLE_TYPE);
    const selectedInactiveRoles = {};
    selectedRolesIds.map(role => {
      if (this.props.inactiveRoles[role.id]) {
        selectedInactiveRoles[role.id] = this.props.inactiveRoles[role.id];
      }
    });
    const allRoles = { ...this.props.oxygen.roles, ...selectedInactiveRoles };
    return {
      ...this.props.oxygen,
      roles: allRoles,
    };
  }

  render() {
    return (
      <AssignedToDropdown
        isEdit
        isMulti
        currentValue={this.state.selected}
        editable
        specialOptions={this.specialOptions}
        placeholder={T.translate('filter.assignedTo')}
        oxygen={featureFlags.get(FEATURE_FLAGS.RFIS_CUSTOM_ROLES) ? this.getAllOptions() : this.props.oxygen}
        className="react-select__assign-admin"
        onSelect={this.handleChange}
        onBlur={this.handleBlur}
      />
    );
  }
}
AdminAssignedToDropdown.displayName = 'AdminAssignedToDropdown';

AdminAssignedToDropdown.defaultProps = {
  serverData: [],
};

AdminAssignedToDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  oxygen: objectType.isRequired,
  serverData: arrayType,
  saveAssignees: PropTypes.func.isRequired,
  inactiveRoles: objectType.isRequired,
};

const mapStateToProps = (state) => ({
  oxygen: featureFlags.get(FEATURE_FLAGS.RFIS_CUSTOM_ROLES) ? getCurrentProjectEntitiesWithActiveRoles(state) : getOxygenMappings(state),
  inactiveRoles: getInactiveRoles(state),
});

export default connect(mapStateToProps)(AdminAssignedToDropdown);
